import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCookieConsent } from '../hooks/useCookieConsent';
import ReceiptPreview from '../components/landing/ReceiptPreview';

const Icon: React.FC<{ path: string; className?: string }> = ({
  path,
  className = 'w-5 h-5',
}) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path d={path} />
  </svg>
);

const ICONS = {
  document:
    'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  offline:
    'M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M8.464 15.536a5 5 0 01-1.414-2.83M5.636 18.364a9 9 0 01-2.167-9.238M12 12h.01M3 3l18 18',
  users:
    'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  chart:
    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  globe:
    'M21 12a9 9 0 11-18 0 9 9 0 0118 0zM3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18 15 15 0 010-18z',
  check: 'M5 13l4 4L19 7',
  arrow: 'M13 7l5 5m0 0l-5 5m5-5H6',
} as const;

const FEATURES = [
  {
    icon: ICONS.document,
    title: 'Kwity skupu',
    body: 'Numer nadaje się sam w formacie 03/15/03/2026, więc nie zdarzy Ci się dwa razy ten sam. Gotowy formularz przyjęcia odpadów metali wychodzi z aplikacji jako PDF do wydruku i podpisu.',
  },
  {
    icon: ICONS.offline,
    title: 'Tryb offline',
    body: 'Brak zasięgu przy wadze nie zatrzymuje pracy. Kwity zapisują się na urządzeniu i same trafiają na serwer, gdy łącze wróci.',
  },
  {
    icon: ICONS.users,
    title: 'Baza klientów',
    body: 'Stałego dostawcę wpisujesz raz — przy kolejnym kwicie wybierasz go z listy razem z adresem i numerem dokumentu. Historia każdego klienta zostaje pod ręką.',
  },
  {
    icon: ICONS.chart,
    title: 'Statystyki, ceny i eksport',
    body: 'Wykresy obrotu, porównanie okresów i zapis każdej zmiany ceny skupu. Zestawienia filtrujesz po dacie, kliencie albo towarze i pobierasz jako PDF lub Excel.',
  },
];

const STEPS = [
  {
    title: 'Wybierasz klienta',
    body: 'Z listy albo dodajesz nowego bez wychodzenia z kwitu.',
  },
  {
    title: 'Wpisujesz towar i wagę',
    body: 'Cena podpowiada się z Twojego cennika, wartość przelicza się sama.',
  },
  {
    title: 'Drukujesz i wypłacasz',
    body: 'Kwit idzie do PDF i do historii, kwota do wypłaty jest już policzona.',
  },
];

const PLAN_INCLUDES = [
  'Nieograniczona liczba kwitów',
  'Klienci, produkty i cennik',
  'Statystyki i historia cen',
  'Wizytówka z cennikiem online',
  'Tryb offline z synchronizacją',
  'Eksport do PDF i Excela',
];

const FAQ = [
  {
    q: 'Czy muszę podawać kartę, żeby zacząć?',
    a: 'Nie. Zakładasz konto, uzupełniasz dane firmy i przez 30 dni korzystasz ze wszystkich funkcji. O płatności rozmawiamy dopiero wtedy, gdy zdecydujesz się zostać.',
  },
  {
    q: 'Co, jeśli w skupie nie ma zasięgu?',
    a: 'Pracujesz dalej. Aplikacja trzyma dane na urządzeniu i wysyła je na serwer, kiedy połączenie wróci — nie musisz niczego zapisywać ani wysyłać ręcznie.',
  },
  {
    q: 'Czy klienci zobaczą moje ceny?',
    a: 'Tylko jeśli sam włączysz wizytówkę. Domyślnie jest wyłączona, a Ty decydujesz, jaki adres ma mieć i co się na niej znajdzie. W każdej chwili możesz ją wyłączyć.',
  },
  {
    q: 'Czy moje dane są tylko moje?',
    a: 'Tak. Każde konto widzi wyłącznie własne kwity, klientów i produkty. Całą bazę możesz wyeksportować do Excela, a konto razem z danymi usunąć z poziomu ustawień.',
  },
  {
    q: 'Czy mogę zrezygnować?',
    a: 'W dowolnym momencie, bez okresu wypowiedzenia. Subskrypcję kończysz jednym kliknięciem, a dostęp zostaje do końca opłaconego miesiąca.',
  },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { reset: resetCookieConsent } = useCookieConsent();

  // Redirect authenticated users to dashboard
  React.useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  if (loading) return null;

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="#top" className="flex items-center gap-2.5">
            <img
              src="/icon-192x192.png"
              alt="Złomiarz"
              className="h-8 w-8 rounded-lg"
            />
            <span className="text-lg font-bold tracking-tight">Złomiarz</span>
          </a>

          <nav
            aria-label="Sekcje strony"
            className="hidden items-center gap-8 text-sm text-gray-600 md:flex"
          >
            <a href="#funkcje" className="hover:text-gray-900">
              Funkcje
            </a>
            <a href="#cennik" className="hover:text-gray-900">
              Cennik
            </a>
            <a href="#pytania" className="hover:text-gray-900">
              Pytania
            </a>
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/login"
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Zaloguj się
            </Link>
            <Link
              to="/register"
              className="hidden rounded-lg bg-orange-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-orange-800 sm:block"
            >
              Wypróbuj za darmo
            </Link>
          </div>
        </div>
      </header>

      <main id="top">
        {/* Hero */}
        <section className="border-b border-gray-200 bg-gray-50">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:grid lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-8 lg:py-24">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-orange-700">
                Dla punktów skupu złomu
              </p>
              {/* The app name belongs in the h1: it is what identifies the
                  product to readers and to checks that compare the homepage
                  against a configured application name. */}
              <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">
                Złomiarz — wystaw kwit w minutę.
                <br />
                <span className="text-orange-700">Nawet bez zasięgu.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">
                Złomiarz zastępuje zeszyt z kwitami i arkusz z cenami.
                Wypełniasz formularz przyjęcia odpadów metali, drukujesz PDF, a
                wieczorem dokładnie wiesz, ile przyjąłeś i ile wypłaciłeś.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-700 px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-orange-800"
                >
                  Zacznij 30 dni za darmo
                  <Icon path={ICONS.arrow} className="h-4 w-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-6 py-3.5 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Mam już konto
                </Link>
              </div>

              <p className="mt-5 text-sm text-gray-500">
                Bez karty płatniczej · Potem 150 zł netto/mies. · Rezygnujesz
                kiedy chcesz
              </p>
            </div>

            <div className="mt-16 lg:mt-0">
              <ReceiptPreview />
            </div>
          </div>
        </section>

        {/* Features */}
        <section
          id="funkcje"
          aria-labelledby="funkcje-heading"
          className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8"
        >
          <h2
            id="funkcje-heading"
            className="max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl"
          >
            Zbudowany pod codzienną pracę skupu
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-gray-600">
            Nie kolejny uniwersalny system do wszystkiego, tylko narzędzie do
            jednej roboty: przyjmowania złomu i pilnowania, co z tego wychodzi.
          </p>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {/* Storefront — the feature nothing else here does */}
            <article className="flex flex-col justify-between rounded-xl border border-orange-200 bg-orange-50 p-6 sm:col-span-2">
              <div>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-700 text-white">
                  <Icon path={ICONS.globe} className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">
                  Wizytówka z cennikiem online
                </h3>
                <p className="mt-2 max-w-xl leading-relaxed text-gray-700">
                  Klienci dzwonią i pytają, ile dziś za miedź? Włącz publiczną
                  stronę swojego skupu: aktualne ceny, godziny otwarcia, dojazd
                  i odpowiedzi na najczęstsze pytania. Kod QR wydrukujesz i
                  powiesisz przy wadze.
                </p>
              </div>
              <p className="mt-5 inline-flex w-fit items-center rounded-lg border border-orange-200 bg-white px-3 py-1.5 font-mono text-sm text-orange-800">
                /p/twoj-skup
              </p>
            </article>

            {FEATURES.map(feature => (
              <article
                key={feature.title}
                className="rounded-xl border border-gray-200 bg-white p-6"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-orange-700">
                  <Icon path={feature.icon} className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{feature.title}</h3>
                <p className="mt-2 leading-relaxed text-gray-600">
                  {feature.body}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* Flow */}
        <section
          aria-labelledby="kroki-heading"
          className="border-y border-gray-200 bg-gray-50"
        >
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
            <h2
              id="kroki-heading"
              className="text-2xl font-bold tracking-tight sm:text-3xl"
            >
              Od wagi do wypłaty w trzech krokach
            </h2>
            <ol className="mt-10 grid gap-8 sm:grid-cols-3">
              {STEPS.map((step, index) => (
                <li
                  key={step.title}
                  className="border-t-2 border-orange-700 pt-4"
                >
                  <p className="text-sm font-bold tabular-nums text-orange-700">
                    {String(index + 1).padStart(2, '0')}
                  </p>
                  <h3 className="mt-1 font-semibold">{step.title}</h3>
                  <p className="mt-1 text-gray-600">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Pricing */}
        <section
          id="cennik"
          aria-labelledby="cennik-heading"
          className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8"
        >
          <div className="lg:grid lg:grid-cols-2 lg:items-center lg:gap-16">
            <div>
              <h2
                id="cennik-heading"
                className="text-3xl font-bold tracking-tight sm:text-4xl"
              >
                Jedna cena, wszystkie funkcje
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-gray-600">
                Bez pakietów, limitów kwitów i dopłat za kolejnego użytkownika.
                Zaczynasz od miesiąca za darmo — jeśli nie podejdzie, po prostu
                nie przedłużasz.
              </p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {PLAN_INCLUDES.map(item => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-gray-700"
                  >
                    <span className="mt-0.5 text-orange-700">
                      <Icon path={ICONS.check} className="h-5 w-5" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-12 rounded-2xl border border-gray-200 bg-gray-50 p-8 lg:mt-0">
              <p className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                Złomiarz Pro
              </p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-5xl font-extrabold tracking-tight">
                  30 dni
                </span>
                <span className="text-lg text-gray-600">za darmo</span>
              </div>
              <p className="mt-3 text-gray-600">
                Potem{' '}
                <span className="font-semibold text-gray-900">
                  150 zł netto
                </span>{' '}
                miesięcznie (184,50 zł brutto z 23% VAT).
              </p>

              <Link
                to="/register"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-700 px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-orange-800"
              >
                Załóż konto
                <Icon path={ICONS.arrow} className="h-4 w-4" />
              </Link>

              <ul className="mt-6 space-y-2 text-sm text-gray-500">
                <li>Na start nie podajesz karty płatniczej.</li>
                <li>Fakturę VAT za subskrypcję wystawiamy automatycznie.</li>
                <li>Rezygnujesz w dowolnym momencie.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section
          id="pytania"
          aria-labelledby="pytania-heading"
          className="border-t border-gray-200 bg-gray-50"
        >
          <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
            <h2
              id="pytania-heading"
              className="text-3xl font-bold tracking-tight sm:text-4xl"
            >
              Częste pytania
            </h2>
            <div className="mt-10 max-w-3xl divide-y divide-gray-200 border-y border-gray-200">
              {FAQ.map(item => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold marker:content-none">
                    {item.q}
                    <span className="shrink-0 text-gray-400 transition-transform group-open:rotate-45">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                    </span>
                  </summary>
                  <p className="mt-3 max-w-2xl leading-relaxed text-gray-600">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="gap-8 sm:flex sm:items-start sm:justify-between">
            <div className="max-w-xs">
              <div className="flex items-center gap-2.5">
                <img
                  src="/icon-192x192.png"
                  alt="Złomiarz"
                  className="h-8 w-8 rounded-lg"
                />
                <span className="text-lg font-bold tracking-tight">
                  Złomiarz
                </span>
              </div>
              <p className="mt-3 text-sm text-gray-500">
                Aplikacja do prowadzenia punktu skupu złomu — kwity, klienci,
                ceny i statystyki.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-8 text-sm sm:mt-0">
              <div>
                <h2 className="font-semibold text-gray-900">Produkt</h2>
                <ul className="mt-3 space-y-2 text-gray-500">
                  <li>
                    <a href="#funkcje" className="hover:text-gray-900">
                      Funkcje
                    </a>
                  </li>
                  <li>
                    <a href="#cennik" className="hover:text-gray-900">
                      Cennik
                    </a>
                  </li>
                  <li>
                    <Link to="/register" className="hover:text-gray-900">
                      Rejestracja
                    </Link>
                  </li>
                  <li>
                    <Link to="/login" className="hover:text-gray-900">
                      Logowanie
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Dokumenty</h2>
                <ul className="mt-3 space-y-2 text-gray-500">
                  <li>
                    <Link to="/terms" className="hover:text-gray-900">
                      Regulamin
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="hover:text-gray-900">
                      Polityka prywatności
                    </Link>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={resetCookieConsent}
                      className="hover:text-gray-900"
                    >
                      Ustawienia cookies
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <p className="mt-10 border-t border-gray-200 pt-6 text-xs text-gray-400">
            © {new Date().getFullYear()} Złomiarz
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
